import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useWorkspace } from '@/features/workspaces';
import { PeriodType } from '@/lib/api/generated/model';
import type { WorkspaceLimitRow } from '@/lib/api/generated/model';

import { exceedsWorkspaceLimitFor } from '../blockedForecast';
import { useSetWorkflowLimit } from '../hooks/useLimits';
import { parseLimitAmount } from '../limitAmount';
import { LimitField } from './LimitField';
import { PeriodField } from './PeriodField';

/**
 * Cap a workflow that does not have one yet (spec 080 FR-004).
 *
 * **Two steps, and the shape is forced.** There is no organization-wide list of
 * workflows and no `GET` for a workspace's workflows at all — they arrive only inside the
 * workspace aggregate (research R9). So the administrator names a workspace first, and
 * that one aggregate is fetched on demand. A fan-out across every workspace to populate a
 * dropdown would be the alternative, and it is worse at every size.
 *
 * The console is trusted with workflow identity already: the service puts `workflow_name`
 * and `workspace_name` on every row of the table this dialog adds to. Nothing here shows
 * a workspace's files, tasks or secrets, and nothing offers a route into them (`072`
 * FR-012).
 *
 * Workflows that already have a cap are not offered — a second cap on one workflow is not
 * a thing that can exist, and offering it would mean silently editing the first.
 */
export function AddWorkflowLimitDialog({
  workspaces,
  cappedWorkflowIds,
  currency,
  fixedWorkspaceId,
}: {
  workspaces: readonly WorkspaceLimitRow[];
  /** Already capped, so not offered again. */
  cappedWorkflowIds: ReadonlySet<string>;
  currency: string;
  /**
   * Opened from one workspace's limits (spec 117): the workspace is already chosen, so
   * the picker is not shown and the trigger names the act rather than the scope.
   */
  fixedWorkspaceId?: string;
}) {
  const [open, setOpen] = useState(false);
  const [workspaceId, setWorkspaceId] = useState<string>(
    fixedWorkspaceId ?? '',
  );
  const [workflowId, setWorkflowId] = useState<string>('');
  const [draft, setDraft] = useState('');
  // A new cap has no prior period of its own to seed from — Monthly, matching the
  // fallback used everywhere else a period is unknown (spec 091 FR-013).
  const [periodDraft, setPeriodDraft] = useState<PeriodType>(
    PeriodType.monthly,
  );
  const [submitted, setSubmitted] = useState(false);
  const setLimit = useSetWorkflowLimit();

  const parsed = parseLimitAmount(draft);
  const amountError = submitted && !parsed.ok ? parsed.reason : null;
  const cannotFire =
    parsed.ok &&
    workspaceId !== '' &&
    exceedsWorkspaceLimitFor(workspaceId, workspaces, parsed.amount);

  const close = (next: boolean) => {
    if (setLimit.isPending) return;
    setOpen(next);
    if (!next) {
      setLimit.reset();
      setSubmitted(false);
      setWorkspaceId(fixedWorkspaceId ?? '');
      setWorkflowId('');
      setDraft('');
      setPeriodDraft(PeriodType.monthly);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogTrigger
        render={
          <Button
            size="sm"
            variant={fixedWorkspaceId ? 'outline' : 'default'}
          />
        }
      >
        {fixedWorkspaceId ? 'Add a workflow cap' : 'Add a workflow limit'}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Cap a workflow</DialogTitle>
          <DialogDescription>
            A workflow limit applies on top of its workspace&rsquo;s — a run
            must clear both. A workflow with no limit of its own is bounded only
            by its workspace.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            setSubmitted(true);
            if (!parsed.ok || workflowId === '') return;
            setLimit.mutate(
              { workflowId, amount: parsed.amount, periodType: periodDraft },
              {
                onSuccess: () => {
                  close(false);
                },
              },
            );
          }}
        >
          {fixedWorkspaceId ? null : (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="workflow-limit-workspace">Workspace</Label>
              <Select
                value={workspaceId}
                onValueChange={(next) => {
                  // The primitive reports a cleared selection as null; '' is this form's
                  // "nothing chosen", and one representation beats two.
                  setWorkspaceId(next ?? '');
                  // The previous choice belongs to a different workspace.
                  setWorkflowId('');
                }}
              >
                <SelectTrigger id="workflow-limit-workspace">
                  {/* A children function on `SelectValue` always wins over its
                    `placeholder` prop in Base UI, regardless of whether a value is
                    selected — so the placeholder case has to be handled inside the
                    function itself. Without it, the trigger showed the workspace's raw
                    id (the select's `value`) instead of its name once one was chosen. */}
                  <SelectValue>
                    {(id: string | undefined) =>
                      id
                        ? (workspaces.find((row) => row.workspace_id === id)
                            ?.workspace_name ?? id)
                        : 'Choose a workspace'
                    }
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {workspaces.map((row) => (
                    <SelectItem key={row.workspace_id} value={row.workspace_id}>
                      {row.workspace_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {workspaceId === '' ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="workflow-limit-workflow">Workflow</Label>
              <Select value="" disabled>
                <SelectTrigger id="workflow-limit-workflow">
                  <SelectValue placeholder="Choose a workspace first" />
                </SelectTrigger>
                <SelectContent />
              </Select>
            </div>
          ) : (
            <WorkflowChoice
              workspaceId={workspaceId}
              value={workflowId}
              onChange={setWorkflowId}
              cappedWorkflowIds={cappedWorkflowIds}
            />
          )}

          <LimitField
            label="Amount per period"
            value={draft}
            onChange={(next) => {
              setDraft(next);
              setSubmitted(false);
            }}
            currency={currency}
            error={amountError}
          />

          <PeriodField value={periodDraft} onChange={setPeriodDraft} />

          {/*
            FR-008, and it has to be said here too. A cap set at or above its
            workspace's can never fire, and an administrator setting a *new* one is no
            less entitled to know that than one changing an existing one — the Change
            dialog said it and this did not, so the same amount warned or stayed silent
            depending on which button had been pressed.
          */}
          {cannotFire ? (
            <p
              role="status"
              className="border-border text-muted-foreground rounded-md border border-dashed px-3 py-2 text-sm"
            >
              This is at or above what that workspace may spend, so it can never
              be the limit that stops a run. The workspace&rsquo;s own limit
              applies first.
            </p>
          ) : null}

          {setLimit.isError ? (
            <p role="alert" className="text-destructive text-sm">
              {setLimit.error.message}
            </p>
          ) : null}

          <DialogFooter>
            <DialogClose
              render={
                <Button variant="outline" disabled={setLimit.isPending} />
              }
            >
              Cancel
            </DialogClose>
            <Button
              type="submit"
              disabled={setLimit.isPending || workflowId === ''}
            >
              {setLimit.isPending ? 'Saving…' : 'Set limit'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The workflows of one chosen workspace.
 *
 * Its own component so the aggregate request exists only once a workspace has been
 * named — a hook cannot be called conditionally, and fetching every workspace's
 * aggregate to keep one call site tidy is the thing research R9 rejected.
 */
function WorkflowChoice({
  workspaceId,
  value,
  onChange,
  cappedWorkflowIds,
}: {
  workspaceId: string;
  value: string;
  onChange: (next: string) => void;
  cappedWorkflowIds: ReadonlySet<string>;
}) {
  const workspace = useWorkspace(workspaceId);
  // Failure has two shapes and both must count, exactly as `useWorkflows` documents:
  // a non-200 resolves the query to `null` (the queryFn maps it), while a request that
  // never gets a response at all throws and sets `isError`. Reading only `isError` left
  // a 404 or a 500 rendering as "no workflows", which invites an administrator to go
  // looking elsewhere for a workflow that is right there.
  const loadFailed =
    !workspace.isPending && (workspace.isError || workspace.data === null);
  const workflows = workspace.data?.workflows ?? [];
  const candidates = workflows.filter(
    (workflow) => !cappedWorkflowIds.has(workflow.id),
  );

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="workflow-limit-workflow">Workflow</Label>
      <Select
        value={value}
        onValueChange={(next) => {
          onChange(next ?? '');
        }}
        disabled={loadFailed || candidates.length === 0}
      >
        <SelectTrigger id="workflow-limit-workflow">
          {/* Same reasoning as the workspace select above: a children function
              always wins over `placeholder` in Base UI, so the placeholder has to be
              produced inside it — otherwise the trigger showed the workflow's raw id
              once one was chosen, instead of its name. */}
          <SelectValue>
            {(id: string | undefined) => {
              if (id) {
                return (
                  candidates.find((workflow) => workflow.id === id)?.name ?? id
                );
              }
              return workspace.isPending
                ? 'Loading workflows…'
                : loadFailed
                  ? 'Could not read this workspace'
                  : // Three states, not two. "This workspace has no workflows" and
                    // "every one of them is already capped" send an administrator to
                    // different places, and an empty dropdown says neither.
                    workflows.length === 0
                    ? 'This workspace has no workflows'
                    : candidates.length === 0
                      ? 'Every workflow here already has a limit'
                      : 'Choose a workflow';
            }}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {candidates.map((workflow) => (
            <SelectItem key={workflow.id} value={workflow.id}>
              {workflow.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {/*
        A workspace whose workflows could not be read is said plainly rather than
        rendered as "no workflows" — the two mean opposite things to someone deciding
        whether to look elsewhere.
      */}
      {loadFailed ? (
        <p role="alert" className="text-destructive text-sm">
          That workspace&rsquo;s workflows could not be read just now.
        </p>
      ) : null}
    </div>
  );
}
